import { Divider } from '@/design-system/components/Divider';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniInput } from '@/design-system/components/inputs';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody } from '@/design-system/components/typography';
import { LayoutContext } from 'contexts/LayoutContext';
import { ActivityIndicator, FlatList, Pressable, ScrollView, SxProp, View } from 'dripsy';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTransferPeer } from '@/features/bill-payment/hooks/useUnionPay.hook';
import { useNavigation } from '@react-navigation/native';
import { RootStackRoutes } from 'types';
import { BillPaymentStackRoutes } from '@/features/bill-payment/payment.navigator';
import { UnionPayStackRoutes } from '@/features/bill-payment/screens/union-pay/type';

export interface SelectAccountWebScreenProps {
  onLayout: (event: any) => void;
  onSelect?: (data: any) => void;
  onBack?: () => void;
  onChangeView?: () => void;
}

export const SelectAccountWebScreen: React.FC<SelectAccountWebScreenProps> = (props) => {
  const { onLayout, onSelect, onBack, onChangeView } = props;
  const layout = useContext(LayoutContext);
  const navigationAny = useNavigation<any>();
  const { t } = useTranslation('bill-payment-union-pay');

  const [search, setSearch] = useState<string | undefined>(undefined);
  const { transferPeers, isLoading } = useTransferPeer({ types: ['UNIONPAY'], current: 1, pageSize: 10, label: search });

  const [viewShow, setViewShow] = useState(false);
  const onChangeViewLocal = () => {
    setViewShow(!viewShow);
  };

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <View sx={{ height: 70, justifyContent: 'center' }}>
        <ModalHeader
          title={t('unionPay::payment-instruction.title--select-account')}
          closeModal={() => {
            onBack && onBack();
          }}
        />
      </View>
      <ScrollView sx={sxStyles.scrollView} showsVerticalScrollIndicator={false}>
        <View sx={sxStyles.body}>
          <View sx={sxStyles.card}>
            <View sx={sxStyles.table}>
              <View sx={sxStyles.operations}>
                <View sx={sxStyles.filters}>
                  <UniInput
                    sx={sxStyles.filter_search}
                    size="default"
                    prefixIcon="search"
                    placeholder={t('unionPay::payment-instruction.text--select-account-account-name-no')}
                    onValueChange={(value) => setSearch(value)}
                  />
                </View>
              </View>
              <FlatList
                data={transferPeers}
                renderItem={({ item }) => {
                  return (
                    <Pressable
                      onPress={() => {
                        onSelect &&
                          onSelect({
                            ...item,
                            accountLabel:
                              item?.label || `${item?.attributes?.receiverFirstName || ''} ${item?.attributes?.receiverSurname || ''}`,
                            accountNumber: item?.attributes?.receiverAccountNumber
                          });
                        onBack && onBack();
                        onChangeView && onChangeView();
                      }}>
                      <View sx={{ paddingY: '$3', paddingX: '$4', gap: '$1' }}>
                        <TextBody sx={{ color: '$textPrimary' }}>
                          {item?.label || `${item?.attributes?.receiverFirstName || ''} ${item?.attributes?.receiverSurname || ''}`}
                        </TextBody>
                        <TextBody sx={{ color: '$textPrimary' }}>{item?.attributes?.receiverAccountNumber}</TextBody>
                      </View>
                    </Pressable>
                  );
                }}
              />
              {isLoading && (
                <View sx={{ padding: '$5' }}>
                  <ActivityIndicator size="large" color={'$disable'} />
                </View>
              )}
            </View>
            {viewShow && <View sx={{ height: '$1', width: '100%' }} />}
            <View sx={{ paddingX: '$4' }}>
              <Divider sx={{ margin: 'auto', backgroundColor: '$borderField', width: '100%' }} />
              <View sx={{ paddingY: '$6', gap: '$1' }}>
                <Pressable
                  onPress={() => {
                    navigationAny.navigate(RootStackRoutes.BillPayment, {
                      screen: BillPaymentStackRoutes.UnionPay,
                      params: {
                        screen: UnionPayStackRoutes.AddNewAccount,
                        onLayout,
                        onBack,
                        onAccountSubmit: (flag: boolean, data: any) => {
                          if (flag) {
                            onSelect && onSelect(data);
                            onBack && onBack();
                          }
                        },
                        onChangeView: onChangeViewLocal
                      }
                    });
                  }}
                  sx={{ flexDirection: 'row', gap: '$1', alignItems: 'center' }}>
                  <Icon name="addAccount" width={20} height={20} />
                  <TextBody sx={{ color: '$textHyperlink' }}>{t('unionPay::payment-instruction.title--add-new-account')}</TextBody>
                </Pressable>
              </View>
              <Divider sx={{ margin: 'auto', backgroundColor: '$borderField', width: '100%' }} />
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  scrollView: {
    // padding: '$3'
  },
  header: {
    flexDirection: 'column'
  },
  tab: {
    backgroundColor: 'transparent',
    width: '98%'
  },
  body: {
    mt: '$1'
  },
  card: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '$4',
    borderRadius: '$radius0',
    boxShadow: 'none',
    paddingY: '$5',
    backgroundColor: '$white'
  },
  table: {
    flexDirection: 'column',
    gap: '$4'
  },
  operations: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    px: '$4',
    zIndex: 1
  },
  filters: {
    flexDirection: 'row',
    gap: '$5',
    width: '100%'
  },
  filter_search: {
    width: '100%'
  }
};
